import { Loader, Timeline } from '@mantine/core'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import { useQuery } from '@redwoodjs/web'

import DnDrawer from 'src/components/Core/DnDrawer'
import { GET_CUSTOM_FIELD_AUDIT_TRAIL } from 'src/graphql/customFields.graphql'
import { buildFullName } from 'src/lib/formatters'

dayjs.extend(relativeTime)

// Helper to render option changes
const renderOptionChanges = (prevOptions, newOptions) => {
  if (!prevOptions && !newOptions) return null

  const prev = prevOptions || []
  const next = newOptions || []

  // If both are empty, no changes
  if (prev.length === 0 && next.length === 0) return null

  const changes = []

  // Find added options
  const added = next.filter(
    (newOpt) => !prev.some((prevOpt) => prevOpt.value === newOpt.value)
  )
  if (added.length > 0) {
    changes.push(
      <div key="added" className="ml-4 mt-1 text-xs">
        <span className="font-medium text-green-700">Added:</span>
        <ul className="ml-4 mt-1 list-disc">
          {added.map((opt, i) => (
            <li key={i}>{opt.label}</li>
          ))}
        </ul>
      </div>
    )
  }

  // Find removed options
  const removed = prev.filter(
    (prevOpt) => !next.some((newOpt) => newOpt.value === prevOpt.value)
  )
  if (removed.length > 0) {
    changes.push(
      <div key="removed" className="ml-4 mt-1 text-xs">
        <span className="font-medium text-red-700">Removed:</span>
        <ul className="ml-4 mt-1 list-disc">
          {removed.map((opt, i) => (
            <li key={i}>{opt.label}</li>
          ))}
        </ul>
      </div>
    )
  }

  // Find modified options (same value, different label)
  const modified = next
    .map((newOpt) => {
      const prevOpt = prev.find((p) => p.value === newOpt.value)
      if (prevOpt && prevOpt.label !== newOpt.label) {
        return { prev: prevOpt, next: newOpt }
      }
      return null
    })
    .filter(Boolean)

  if (modified.length > 0) {
    changes.push(
      <div key="modified" className="ml-4 mt-1 text-xs">
        <span className="font-medium text-blue-700">Modified:</span>
        <ul className="ml-4 mt-1 list-disc">
          {modified.map((change, i) => (
            <li key={i}>
              <span className="line-through">{change.prev.label}</span> →{' '}
              <span className="font-medium">{change.next.label}</span>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  return changes.length > 0 ? <div className="mt-2">{changes}</div> : null
}

// Helper to format stage names
const formatStageName = (stage) => {
  return stage
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ')
}

// Helper to render stage changes
const renderStageChanges = (prevStages, newStages) => {
  if (!prevStages && !newStages) return null

  const prev = prevStages || []
  const next = newStages || []

  // If both are empty or identical, no changes
  if (
    JSON.stringify(prev.sort()) === JSON.stringify(next.sort())
  ) {
    return null
  }

  const changes = []

  // Find added stages
  const added = next.filter((stage) => !prev.includes(stage))
  if (added.length > 0) {
    changes.push(
      <div key="added-stages" className="ml-8 mt-1 text-xs">
        <span className="font-medium text-green-700">Added stages:</span>
        <ul className="ml-4 mt-1 list-disc">
          {added.map((stage, i) => (
            <li key={i}>{formatStageName(stage)}</li>
          ))}
        </ul>
      </div>
    )
  }

  // Find removed stages
  const removed = prev.filter((stage) => !next.includes(stage))
  if (removed.length > 0) {
    changes.push(
      <div key="removed-stages" className="ml-8 mt-1 text-xs">
        <span className="font-medium text-red-700">Removed stages:</span>
        <ul className="ml-4 mt-1 list-disc">
          {removed.map((stage, i) => (
            <li key={i}>{formatStageName(stage)}</li>
          ))}
        </ul>
      </div>
    )
  }

  return changes.length > 0 ? <div>{changes}</div> : null
}

// Helper to render other field changes
const renderFieldChanges = (log) => {
  const changes = []

  if (log.prevLabel !== null && log.prevLabel !== log.newLabel) {
    changes.push(
      <div key="label" className="ml-4 mt-1 text-xs">
        <span className="font-medium">Label:</span>{' '}
        <span className="line-through">{log.prevLabel}</span> →{' '}
        <span className="font-medium">{log.newLabel}</span>
      </div>
    )
  }

  if (log.prevIsRequired !== null && log.prevIsRequired !== log.newIsRequired) {
    changes.push(
      <div key="required" className="ml-4 mt-1 text-xs">
        <span className="font-medium">Required:</span>{' '}
        <span className="line-through">{log.prevIsRequired ? 'Yes' : 'No'}</span>{' '}
        → <span className="font-medium">{log.newIsRequired ? 'Yes' : 'No'}</span>
      </div>
    )
  }

  // Show required for stages changes
  const stageChanges = renderStageChanges(
    log.prevRequiredForStages,
    log.newRequiredForStages
  )
  if (stageChanges) {
    changes.push(
      <div key="required-stages" className="ml-4 mt-1 text-xs">
        <span className="font-medium">Required for stages:</span>
        {stageChanges}
      </div>
    )
  }

  if (
    log.prevIsSearchable !== null &&
    log.prevIsSearchable !== log.newIsSearchable
  ) {
    changes.push(
      <div key="searchable" className="ml-4 mt-1 text-xs">
        <span className="font-medium">Searchable:</span>{' '}
        <span className="line-through">
          {log.prevIsSearchable ? 'Yes' : 'No'}
        </span>{' '}
        → <span className="font-medium">{log.newIsSearchable ? 'Yes' : 'No'}</span>
      </div>
    )
  }

  if (log.prevStatus !== null && log.prevStatus !== log.newStatus) {
    changes.push(
      <div key="status" className="ml-4 mt-1 text-xs">
        <span className="font-medium">Status:</span>{' '}
        <span className="line-through">{log.prevStatus}</span> →{' '}
        <span className="font-medium">{log.newStatus}</span>
      </div>
    )
  }

  return changes.length > 0 ? <div className="mt-2">{changes}</div> : null
}

interface ChangeHistoryDrawerProps {
  opened: boolean
  onClose: () => void
  customFieldId: string | null
  fieldLabel: string | null
}

const ChangeHistoryDrawer = ({
  opened,
  onClose,
  customFieldId,
  fieldLabel,
}: ChangeHistoryDrawerProps) => {
  const { data, loading, error } = useQuery(GET_CUSTOM_FIELD_AUDIT_TRAIL, {
    variables: { customFieldId },
    skip: !customFieldId || !opened,
  })

  const auditTrail = data?.customFieldAuditTrail || []

  return (
    <DnDrawer opened={opened} onClose={onClose}>
      <div className="flex h-full flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold text-doubleNickel-gray-900">
            Change History
          </h2>
          {fieldLabel && (
            <p className="text-sm text-doubleNickel-gray-600">{fieldLabel}</p>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col overflow-auto">
          {loading && (
            <div className="flex flex-1 items-center justify-center">
              <Loader size="sm" color="gray" />
            </div>
          )}

          {error && (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-sm text-red-600">
                Error loading change history
              </p>
            </div>
          )}

          {!loading && !error && auditTrail.length === 0 && (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-sm text-doubleNickel-gray-500">
                No changes recorded
              </p>
            </div>
          )}

          {!loading && !error && auditTrail.length > 0 && (
            <Timeline
              bulletSize={12}
              lineWidth={2}
              classNames={{
                itemTitle: 'h-0',
              }}
            >
              {auditTrail.map((log) => {
                const employeeName = log.employee
                  ? buildFullName(log.employee)
                  : log.employeeId || 'Unknown'

                const hasOptionChanges =
                  log.prevOptions || log.newOptions
                const optionChanges = renderOptionChanges(
                  log.prevOptions,
                  log.newOptions
                )
                const fieldChanges = renderFieldChanges(log)

                return (
                  <Timeline.Item key={log.auditTrailId}>
                    <div className="flex flex-col gap-1">
                      {/* Notes - main message */}
                      <div className="text-sm text-doubleNickel-gray-900">
                        {log.changeType === 'CREATE'
                          ? 'Created custom field'
                          : 'Updated custom field'}
                      </div>

                      {/* Field changes */}
                      {fieldChanges}

                      {/* Option changes */}
                      {hasOptionChanges && optionChanges && (
                        <div className="mt-2">
                          <div className="text-xs font-medium text-doubleNickel-gray-700">
                            Options:
                          </div>
                          {optionChanges}
                        </div>
                      )}

                      {/* Metadata - who and when */}
                      <div className="mt-2 flex flex-wrap gap-1 text-xs text-doubleNickel-gray-500">
                        <span className="font-medium">{employeeName}</span>
                        <span>·</span>
                        <span>{dayjs(log.createdAt).fromNow()}</span>
                        <span>·</span>
                        <span>
                          {dayjs(log.createdAt).format('MMM D, YYYY h:mm A')}
                        </span>
                      </div>
                    </div>
                  </Timeline.Item>
                )
              })}
            </Timeline>
          )}
        </div>
      </div>
    </DnDrawer>
  )
}

export default ChangeHistoryDrawer
